import { Injectable } from '@angular/core';
import { logger } from '@damofire/data-commons';
import { Survey } from '../models/survey.model';

export type SurveyLogAction =
  | 'survey.create'
  | 'survey.save'
  | 'survey.autosave'
  | 'survey.publish'
  | 'survey.delete'
  | 'survey.copy'
  | 'survey.duplicate'
  | 'survey.load'
  | 'survey.restore'
  | 'survey.snapshot'
  | 'survey.sync_choice'
  | 'survey.dump'
  | 'grid.load'
  | 'sheet.create'
  | 'sheet.delete'
  | 'sheet.rename'
  | 'sheet.duplicate'
  | 'sheet.save'
  | 'location.add'
  | 'location.edit'
  | 'location.delete'
  | 'location.move'
  | 'location.duplicate'
  | 'location.save'
  | 'photo.add'
  | 'photo.delete';

const HIGH_FREQUENCY_ACTIONS: ReadonlySet<SurveyLogAction> = new Set<SurveyLogAction>(['survey.save', 'survey.autosave', 'survey.load', 'sheet.save', 'location.edit', 'location.save']);

export type SurveyLogSource = 'idb' | 'firestore';

export interface SurveyCounts {
  sheets: number;
  locations: number;
}

export interface SurveyLogContext {
  surveyId?: string;
  measurementIndex?: number;
  source?: SurveyLogSource;
  before?: SurveyCounts;
  after?: SurveyCounts;
  correlationId?: string;
  [key: string]: unknown;
}

export function countSurvey(survey: Pick<Survey, 'measurements'> | null | undefined): SurveyCounts {
  const measurements = Object.values(survey?.measurements ?? {}).filter(Boolean);
  const locations = measurements.reduce((total, measurement) => total + (measurement.locationData?.locations?.length ?? 0), 0);
  return { sheets: measurements.length, locations };
}

@Injectable({ providedIn: 'root' })
export class SurveyLogService {
  newCorrelationId(): string {
    return crypto.randomUUID();
  }

  isOnline(): boolean {
    return typeof navigator === 'undefined' ? true : navigator.onLine;
  }

  success(action: SurveyLogAction, context: SurveyLogContext = {}): void {
    const attributes = this.buildAttributes(action, 'success', context);
    if (HIGH_FREQUENCY_ACTIONS.has(action)) {
      logger.debug(`Survey action: ${action}`, attributes);
      return;
    }
    logger.info(`Survey action: ${action}`, attributes);
  }

  failure(action: SurveyLogAction, error: unknown, context: SurveyLogContext = {}): void {
    logger.error(`Survey action failed: ${action}`, { ...this.buildAttributes(action, 'failure', context), error });
  }

  warn(action: SurveyLogAction, message: string, context: SurveyLogContext = {}): void {
    logger.warn(`Survey action warning: ${action} - ${message}`, this.buildAttributes(action, 'warning', context));
  }

  /** Runs `work`, logging success or failure. `work` may fill `before`/`after` counts on the context it receives. */
  async track<T>(action: SurveyLogAction, context: SurveyLogContext, work: (tracked: SurveyLogContext) => Promise<T>): Promise<T> {
    const correlated: SurveyLogContext = { ...context, correlationId: context.correlationId ?? this.newCorrelationId() };
    try {
      const result = await work(correlated);
      this.success(action, correlated);
      return result;
    } catch (error: unknown) {
      this.failure(action, error, correlated);
      throw error;
    }
  }

  private buildAttributes(action: SurveyLogAction, result: 'success' | 'failure' | 'warning', context: SurveyLogContext): Record<string, unknown> {
    return {
      category: 'survey',
      action,
      result,
      online: this.isOnline(),
      ...context,
    };
  }
}
